import { useEffect, useState, type ReactElement } from "react";
import { env } from "../../shared/config/env";
import {
  Avatar,
  Box,
  Chip,
  Container,
  Paper,
  Skeleton,
  Tab,
  Tabs,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import LocalPoliceIcon from "@mui/icons-material/LocalPolice";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined";
import DevicesOutlinedIcon from "@mui/icons-material/DevicesOutlined";
import BadgeIcon from "@mui/icons-material/Badge";
import { useAuth } from "@workos-inc/authkit-react";
import {
  UserProfile,
  UserSecurity,
  UserSessions,
  WorkOsWidgets,
} from "@workos-inc/widgets";
import { getWorkOsWidgetTheme } from "../../shared/workosTheme";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  currentProfileQuery,
  profileKeys,
} from "../../features/users/api/profileQueries";
import { usePageHeader } from "../../context/LayoutContext";
import { useColorMode } from "../../context/ColorModeContext";
import ProfileMetadataForm from "./ProfileMetadataForm";

type AccountTab = "profile" | "security" | "sessions";
interface TabConfig {
  value: AccountTab;
  label: string;
  icon: ReactElement;
  description: string;
}

const WIDGETS_API_HOSTNAME = env.workos.apiHostname;

// Static tab definitions. Hoisted to module scope so the array (and its icon
// elements) is not recreated on every render — this keeps it stable for the
// activeTab-guard effect below without a useMemo.
const availableTabs: TabConfig[] = [
  {
    value: "profile",
    label: "Profile",
    icon: <PersonOutlineIcon fontSize="small" />,
    description: "Personal information and contact details",
  },
  {
    value: "security",
    label: "Security",
    icon: <SecurityOutlinedIcon fontSize="small" />,
    description: "Password and multi-factor authentication",
  },
  {
    value: "sessions",
    label: "Sessions",
    icon: <DevicesOutlinedIcon fontSize="small" />,
    description: "Review and manage active signed-in devices",
  },
];

function getInitials(firstName?: string, lastName?: string): string {
  const first = firstName?.trim()?.[0] ?? "";
  const last = lastName?.trim()?.[0] ?? "";
  return `${first}${last}`.toUpperCase() || "U";
}

const ProfilePage = () => {
  usePageHeader({
    title: "My Profile",
    subtitle: "View and manage your personal information",
    icon: <LocalPoliceIcon />,
  });

  const theme = useTheme();
  const { mode } = useColorMode();
  const { getAccessToken, user } = useAuth();
  const queryClient = useQueryClient();
  const { data: profileData } = useQuery(currentProfileQuery());
  const officer = profileData ?? null;

  const fullName =
    [officer?.firstName, officer?.lastName].filter(Boolean).join(" ") ||
    [user?.firstName, user?.lastName].filter(Boolean).join(" ") ||
    user?.email ||
    "Officer";

  const [activeTab, setActiveTab] = useState<AccountTab>("profile");

  useEffect(() => {
    if (!availableTabs.some((tab) => tab.value === activeTab)) {
      setActiveTab("profile");
    }
  }, [activeTab]);

  const authToken = () => getAccessToken();

  const renderActiveWidget = () => {
    switch (activeTab) {
      case "security":
        return <UserSecurity authToken={authToken} />;
      case "sessions":
        return <UserSessions authToken={authToken} />;
      case "profile":
      default:
        return <UserProfile authToken={authToken} />;
    }
  };

  return (
    <Container sx={{ pb: 4 }}>
      {/* Top Summary / Identity Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* Card 1: Identity */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
              display: "flex",
              alignItems: "center",
              gap: 2,
              height: "100%",
            }}
          >
            <Avatar
              sx={{
                width: 64,
                height: 64,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: "primary.main",
                fontSize: "1.5rem",
                fontWeight: 700,
                border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              }}
            >
              {getInitials(
                officer?.firstName ?? user?.firstName ?? undefined,
                officer?.lastName ?? user?.lastName ?? undefined,
              )}
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                {fullName}
              </Typography>
              {officer ? (
                <Chip
                  label={officer.rank || "Officer"}
                  size="small"
                  sx={{
                    mt: 0.5,
                    height: 24,
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    color: "primary.main",
                    border: "none",
                  }}
                />
              ) : (
                <Skeleton
                  variant="rounded"
                  width={72}
                  height={24}
                  sx={{ mt: 0.5, borderRadius: 12 }}
                />
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Card 2: Organization */}
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
              height: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <LocalPoliceIcon fontSize="small" color="action" />
              <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary", textTransform: "uppercase" }}>
                Station & Dept
              </Typography>
            </Box>
            {officer ? (
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {officer.stationName || "Unknown Station"}
              </Typography>
            ) : (
              <Skeleton variant="text" width="70%" sx={{ fontSize: "1rem" }} />
            )}
            {officer ? (
              <Typography variant="body2" color="text.secondary">
                {officer.department || "Unknown Dept"}
              </Typography>
            ) : (
              <Skeleton variant="text" width="50%" sx={{ fontSize: "0.875rem" }} />
            )}
          </Paper>
        </Grid>

        {/* Card 3: Contact/Badge */}
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
              height: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <BadgeIcon fontSize="small" color="action" />
              <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary", textTransform: "uppercase" }}>
                Badge & Contact
              </Typography>
            </Box>
            {officer ? (
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                #{officer.badgeNumber || "N/A"}
              </Typography>
            ) : (
              <Skeleton variant="text" width="40%" sx={{ fontSize: "1rem" }} />
            )}
            {officer || user?.email ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
                title={officer?.email || user?.email || ""}
              >
                {officer?.email || user?.email || "No Email"}
              </Typography>
            ) : (
              <Skeleton variant="text" width="80%" sx={{ fontSize: "0.875rem" }} />
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Main Content Area */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            px: 3,
            pt: 2,
          }}
        >
          <Tabs
            value={activeTab}
            onChange={(_, value: AccountTab) => setActiveTab(value)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: 48,
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.95rem",
                mr: 3,
                minHeight: 48,
              },
              "& .MuiTab-root:focus": { outline: "none" },
            }}
          >
            {availableTabs.map((tab) => (
              <Tab
                key={tab.value}
                value={tab.value}
                icon={tab.icon}
                iconPosition="start"
                label={tab.label}
              />
            ))}
          </Tabs>
        </Box>

        <Box sx={{ p: 3 }}>
          <WorkOsWidgets apiHostname={WIDGETS_API_HOSTNAME} theme={getWorkOsWidgetTheme(mode === "dark" ? "dark" : "light")}>
            {renderActiveWidget()}
          </WorkOsWidgets>

          {activeTab === "profile" && officer && (
            <ProfileMetadataForm
              officer={officer}
              onSaved={(updated) =>
                // Direct cache write = the old setter's exact semantics: the
                // saved profile is the new truth, no refetch round trip.
                queryClient.setQueryData(profileKeys.current(), updated)
              }
            />
          )}
        </Box>
      </Paper>

    </Container>
  );
};

export default ProfilePage;
